import { http, unwrapData } from '@noor/api';
import { env } from '@noor/config';
import { parseRbacResponse } from '../lib/parse-response';
import {
  routeDetailsSchema,
  fieldRulesSchema,
  fieldRuleSchema,
  grantSchema,
  grantCreateSchema,
  fieldRuleBodySchema,
  routeTitleSchema,
  routeEnvelopeSchema,
  type GrantCreate,
  type FieldRuleBody,
} from '../model/schema';
import { type RouteFilters, type GrantFilters } from '../model/list-params';
import { collectCatalog, rbacCatalogApi } from './rbac-catalog-api';

// RBAC endpoints are served by the configured core API host.
// Bearer credentials and authentication retries use the shared HTTP client.
const options = { baseURL: env.VITE_CORE_API_URL };
const base = '/api/v1/authz';
const routePath = (id: string) => `${base}/routes/${encodeURIComponent(id)}`;
const fieldPath = (id: string, path: string) =>
  `${routePath(id)}/field-rules/${encodeURIComponent(path)}`;
export const rbacApi = {
  async subjects() {
    return {
      subjects: await collectCatalog((params) => rbacCatalogApi.subjects(params)),
    };
  },
  async routes(filters: RouteFilters) {
    return {
      routes: await collectCatalog((params) =>
        rbacCatalogApi.routes({ ...params, ...filters }),
      ),
    };
  },
  async route(id: string) {
    const raw = await http.get<unknown>(routePath(id), options);
    const data = unwrapData(raw);
    return routeDetailsSchema.parse(
      typeof data === 'object' && data !== null && !('route' in data)
        ? { ...data, route: data }
        : data,
    );
  },
  async rename(id: string, title: string) {
    const raw = await http.patch<unknown>(
      routePath(id),
      routeTitleSchema.parse({ title }),
      options,
    );
    const data = unwrapData(raw);
    return routeEnvelopeSchema.parse(
      typeof data === 'object' && data !== null && !('route' in data)
        ? { route: data }
        : data,
    ).route;
  },
  async matrix(filters: RouteFilters) {
    const first = await rbacCatalogApi.matrix({ ...filters, page: 1, pageSize: 100 });
    const routes = [...first.routes];
    for (let page = 2; page <= first.pageCount; page++)
      routes.push(
        ...(await rbacCatalogApi.matrix({ ...filters, page, pageSize: 100 })).routes,
      );
    return { subjects: first.subjects, routes };
  },
  async grants(filters: GrantFilters) {
    return {
      grants: await collectCatalog((params) =>
        rbacCatalogApi.grants({ ...params, ...filters }),
      ),
    };
  },
  async createGrant(body: GrantCreate) {
    return parseRbacResponse(
      grantSchema,
      await http.post<unknown>(
        `${base}/grants`,
        (() => {
          const { route_id, ...recipient } = grantCreateSchema.parse(body);
          return { route_slug: route_id, ...recipient };
        })(),
        options,
      ),
    );
  },
  async deleteGrant(id: number) {
    await http.delete<void>(`${base}/grants/${id}`, options);
  },
  async fieldRules(id: string) {
    return parseRbacResponse(
      fieldRulesSchema,
      await http.get<unknown>(`${routePath(id)}/field-rules`, options),
    );
  },
  async putFieldRule(id: string, path: string, body: FieldRuleBody) {
    return parseRbacResponse(
      fieldRuleSchema,
      await http.put<unknown>(
        fieldPath(id, path),
        fieldRuleBodySchema.parse(body),
        options,
      ),
    );
  },
  async deleteFieldRule(id: string, path: string) {
    await http.delete<void>(fieldPath(id, path), options);
  },
  async audit() {
    const result = await rbacCatalogApi.audit({ page: 1, pageSize: 100 });
    return { entries: result.list, next_before_id: null };
  },
};
